import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe } from '@ngx-translate/core';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { ReleaseService } from '../../../services/release.service';
import {
  ReleaseAssessment,
  ReleaseCriterion,
  ReleaseDecisionValue,
  ReleasePhase,
  ReleaseRecommendation,
  ReleaseSignoff,
} from '../../../models/release-assessment.model';
import { TEST_CASE_PRIORITIES, TestCasePriority } from '../../../models/test-case.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { formatIsoDateDe } from '../../../shared/date.util';

interface AssessmentState {
  loading: boolean;
  error: boolean;
  data: ReleaseAssessment | null;
}

interface AssessmentRequest {
  projectId: string;
  releaseId: string;
}

type PhaseState = 'done' | 'aktuell' | 'offen';

type ActionError = 'failed' | 'forbidden' | 'noDecision' | null;

const PRIORITIES_DESC: readonly TestCasePriority[] = [...TEST_CASE_PRIORITIES].reverse();

const DECISIONS: readonly ReleaseDecisionValue[] = ['GO', 'AUFLAGEN', 'NOGO'];

const DECISION_FOR_RECOMMENDATION: Record<ReleaseRecommendation, ReleaseDecisionValue> = {
  GO: 'GO',
  GO_AUFLAGEN: 'AUFLAGEN',
  NO_GO: 'NOGO',
};

const I18N = 'testResults.freigabe.bewertung.';

const RECOMMENDATION_TEXT = `${I18N}recommendationText.`;

const DECISION_WITH_CONDITIONS: ReleaseDecisionValue = 'AUFLAGEN';

function actionError(error: unknown): ActionError {
  if (!(error instanceof HttpErrorResponse)) return 'failed';
  if (error.status === 403) return 'forbidden';
  return error.status === 409 ? 'noDecision' : 'failed';
}

let nextId = 0;

@Component({
  selector: 'app-freigabe-bewertung',
  standalone: true,
  imports: [TranslatePipe, AvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './freigabe-bewertung.component.html',
  styleUrl: './freigabe-bewertung.component.scss',
})
export class FreigabeBewertungComponent {
  private readonly releaseService = inject(ReleaseService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releaseId = input.required<string>();

  readonly decisionSaved = output<ReleaseAssessment>();

  protected readonly priorities = PRIORITIES_DESC;
  protected readonly decisions = DECISIONS;
  protected readonly commentMax = FIELD_LENGTHS.DECISION_COMMENT_MAX;
  protected readonly idPrefix = `fb-${nextId++}`;

  protected readonly state = signal<AssessmentState>({ loading: false, error: false, data: null });
  protected readonly selection = signal<ReleaseDecisionValue | null>(null);
  protected readonly comment = signal('');
  protected readonly saving = signal(false);
  protected readonly saveError = signal<ActionError>(null);
  protected readonly confirming = signal(false);
  protected readonly confirmError = signal<ActionError>(null);

  private pendingAction = new Subscription();

  protected readonly assessment = computed(() => this.state().data);

  protected readonly deviates = computed(() => {
    const data = this.assessment();
    const chosen = this.selection();
    return !!data && !!chosen && chosen !== DECISION_FOR_RECOMMENDATION[data.empfehlung];
  });

  protected readonly matchesSavedDecision = computed(() => {
    const saved = this.assessment()?.entscheidung;
    return (
      !!saved && this.selection() === saved.entscheidung && this.comment().trim() === (saved.kommentar ?? '').trim()
    );
  });

  readonly hasUnsavedChanges = computed(() => {
    const data = this.assessment();
    if (!data) return false;
    if (data.entscheidung) return !this.matchesSavedDecision();
    return !!this.selection() || this.comment().trim().length > 0;
  });

  protected readonly canSave = computed(
    () => !!this.selection() && !this.saving() && (!this.deviates() || this.comment().trim().length > 0),
  );

  protected readonly commentPlaceholderKey = computed(
    () => I18N + (this.selection() === DECISION_WITH_CONDITIONS ? 'placeholderAuflagen' : 'placeholder'),
  );

  protected readonly saveErrorKey = computed(() => {
    const error = this.saveError();
    if (!error) return null;
    return I18N + (error === 'forbidden' ? 'saveForbidden' : 'saveError');
  });

  protected readonly confirmErrorKey = computed(() => {
    const error = this.confirmError();
    if (!error) return null;
    if (error === 'forbidden') return I18N + 'confirmForbidden';
    return I18N + (error === 'noDecision' ? 'confirmNoDecision' : 'confirmError');
  });

  protected readonly recommendationText = computed(() => {
    const data = this.assessment();
    if (!data) return null;
    if (data.mussVerletzt > 0) {
      return { key: RECOMMENDATION_TEXT + (data.mussVerletzt === 1 ? 'mussOne' : 'muss'), count: data.mussVerletzt };
    }
    if (data.sollVerletzt > 0) {
      return { key: RECOMMENDATION_TEXT + (data.sollVerletzt === 1 ? 'sollOne' : 'soll'), count: data.sollVerletzt };
    }
    return { key: RECOMMENDATION_TEXT + 'alle', count: 0 };
  });

  private readonly request = computed<AssessmentRequest>(() => ({
    projectId: this.projectId(),
    releaseId: this.releaseId(),
  }));

  constructor() {
    this.destroyRef.onDestroy(() => this.pendingAction.unsubscribe());
    toObservable(this.request)
      .pipe(
        tap(() => this.cancelPendingAction()),
        switchMap((req) =>
          this.releaseService.getAssessment(req.projectId, req.releaseId).pipe(
            map((data): AssessmentState => ({ loading: false, error: false, data })),
            catchError(() => of<AssessmentState>({ loading: false, error: true, data: null })),
            startWith<AssessmentState>({ loading: true, error: false, data: null }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        this.state.set(state);
        this.applyDecision(state.data);
      });
  }

  protected choose(decision: ReleaseDecisionValue): void {
    this.selection.set(decision);
    this.saveError.set(null);
  }

  protected onCommentInput(value: string): void {
    this.comment.set(value);
  }

  protected save(): void {
    const entscheidung = this.selection();
    if (!entscheidung || !this.canSave()) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.pendingAction.add(
      this.releaseService
        .saveDecision(this.projectId(), this.releaseId(), { entscheidung, kommentar: this.comment().trim() || null })
        .subscribe({
          next: (data) => {
            this.saving.set(false);
            this.state.set({ loading: false, error: false, data });
            this.applyDecision(data);
            this.decisionSaved.emit(data);
          },
          error: (error: unknown) => {
            this.saving.set(false);
            this.saveError.set(actionError(error));
          },
        }),
    );
  }

  protected confirm(signoff: ReleaseSignoff): void {
    this.confirming.set(true);
    this.confirmError.set(null);
    this.pendingAction.add(
      this.releaseService.confirmSignoff(this.projectId(), this.releaseId(), signoff.rolle).subscribe({
        next: (data) => {
          this.confirming.set(false);
          this.state.set({ loading: false, error: false, data });
        },
        error: (error: unknown) => {
          this.confirming.set(false);
          this.confirmError.set(actionError(error));
        },
      }),
    );
  }

  protected phaseState(phase: ReleasePhase): PhaseState {
    if (phase.done) return 'done';
    return phase.aktuell ? 'aktuell' : 'offen';
  }

  protected formatDate(iso: string | null): string {
    return formatIsoDateDe(iso);
  }

  protected gateTone(criterion: ReleaseCriterion): 'ok' | 'muss' | 'soll' {
    if (criterion.ok) return 'ok';
    return criterion.muss ? 'muss' : 'soll';
  }

  protected percent(aus: number, ges: number): number {
    return ges > 0 ? Math.round((aus / ges) * 100) : 0;
  }

  protected stripes(count: number): number[] {
    return Array.from({ length: Math.max(count, 0) }, (_, i) => i);
  }

  private cancelPendingAction(): void {
    this.pendingAction.unsubscribe();
    this.pendingAction = new Subscription();
    this.saving.set(false);
    this.saveError.set(null);
    this.confirming.set(false);
    this.confirmError.set(null);
  }

  private applyDecision(data: ReleaseAssessment | null): void {
    this.selection.set(data?.entscheidung?.entscheidung ?? null);
    this.comment.set(data?.entscheidung?.kommentar ?? '');
  }
}
